// src/misc/utils/calendarUtils.js

import { safeStr } from "./sheetRowUtils";

/**
 * Pad a number to two digits.
 *
 * @param {number} n
 * @returns {string}
 */
function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Parse a local date + time string into a JavaScript Date object.
 *
 * Accepts:
 * - Date: "MM/DD/YYYY"
 * - Time: "h:mm AM", "h:mm:ss PM", or empty
 *
 * If time is missing or invalid, midnight local time is assumed.
 *
 * @param {string} dateStr - Date string in MM/DD/YYYY format
 * @param {string} timeStr - Time string in h:mm(:ss) AM/PM format
 * @returns {Date|null} Local Date object, or null if parsing fails
 */
export function parseLocalDateTime(dateStr, timeStr) {
  const d = safeStr(dateStr);
  const t = safeStr(timeStr);

  if (!d) return null;

  const [mm, dd, yyyy] = d.split("/").map((x) => parseInt(x, 10));
  if (!mm || !dd || !yyyy) return null;

  // No time provided → midnight local
  if (!t) return new Date(yyyy, mm - 1, dd, 0, 0, 0);

  const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return new Date(yyyy, mm - 1, dd, 0, 0, 0);

  let hh = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] ? parseInt(m[3], 10) : 0;
  const ampm = m[4].toUpperCase();

  if (ampm === "PM" && hh !== 12) hh += 12;
  if (ampm === "AM" && hh === 12) hh = 0;

  return new Date(yyyy, mm - 1, dd, hh, min, sec);
}

/**
 * Convert a Date to an ICS-compatible UTC timestamp.
 *
 * Output format: YYYYMMDDTHHMMSSZ
 *
 * @param {Date} date
 * @returns {string}
 */
function toUtcIcsStamp(date) {
  const y = date.getUTCFullYear();
  const mo = pad2(date.getUTCMonth() + 1);
  const da = pad2(date.getUTCDate());
  const hh = pad2(date.getUTCHours());
  const mm = pad2(date.getUTCMinutes());
  const ss = pad2(date.getUTCSeconds());
  return `${y}${mo}${da}T${hh}${mm}${ss}Z`;
}

/**
 * Escape text for inclusion in an ICS file.
 *
 * @param {string} s
 * @returns {string}
 */
function escapeIcsText(s) {
  return safeStr(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/**
 * Build raw ICS file contents for a calendar event.
 *
 * If endDate is missing, a 1-hour duration is assumed.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.description
 * @param {string} params.location
 * @param {Date} params.startDate
 * @param {Date} [params.endDate]
 * @returns {string|null} ICS file contents, or null if invalid
 */
export function buildIcsContent({
  title,
  description,
  location,
  startDate,
  endDate,
}) {
  const now = new Date();

  const uid = `${Math.random().toString(16).slice(2)}-${Date.now()}@glastonbury-budget`;
  const dtstamp = toUtcIcsStamp(now);

  const start = startDate || null;
  const end =
    endDate ||
    (startDate ? new Date(startDate.getTime() + 60 * 60 * 1000) : null);

  if (!start || !end) return null;

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Glastonbury Budget//America250//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${toUtcIcsStamp(start)}`,
    `DTEND:${toUtcIcsStamp(end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    location ? `LOCATION:${escapeIcsText(location)}` : null,
    description ? `DESCRIPTION:${escapeIcsText(description)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

/**
 * Trigger a browser download of an ICS file.
 *
 * @param {Object} params
 * @param {string} params.filename
 * @param {string} params.icsText
 */
export function downloadIcs({ filename, icsText }) {
  const blob = new Blob([icsText], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

/**
 * Build a Google Calendar event URL.
 *
 * If endDate is missing, a 1-hour duration is assumed.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.description
 * @param {string} params.location
 * @param {Date} params.startDate
 * @param {Date} [params.endDate]
 * @returns {string}
 */
export function buildGoogleCalendarUrl({
  title,
  description,
  location,
  startDate,
  endDate,
}) {
  const start = startDate;
  const end = endDate || new Date(startDate.getTime() + 60 * 60 * 1000);
  const dates = `${toUtcIcsStamp(start)}/${toUtcIcsStamp(end)}`;

  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", safeStr(title));
  url.searchParams.set("dates", dates);
  if (location) url.searchParams.set("location", safeStr(location));
  if (description) url.searchParams.set("details", safeStr(description));
  return url.toString();
}

/**
 * Build a Yahoo Calendar event URL.
 *
 * If endDate is missing, a 1-hour duration is assumed.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.description
 * @param {string} params.location
 * @param {Date} params.startDate
 * @param {Date} [params.endDate]
 * @returns {string}
 */
export function buildYahooCalendarUrl({
  title,
  description,
  location,
  startDate,
  endDate,
}) {
  const start = startDate;
  const end = endDate || new Date(startDate.getTime() + 60 * 60 * 1000);

  const url = new URL("https://calendar.yahoo.com/");
  url.searchParams.set("v", "60");
  url.searchParams.set("view", "d");
  url.searchParams.set("type", "20");
  url.searchParams.set("title", safeStr(title));
  url.searchParams.set("st", toUtcIcsStamp(start));
  url.searchParams.set("et", toUtcIcsStamp(end));
  if (location) url.searchParams.set("in_loc", safeStr(location));
  if (description) url.searchParams.set("desc", safeStr(description));
  return url.toString();
}

/**
 * Format a start/end Date pair into a clean, human-readable string.
 *
 * Examples:
 * - "Sat, Jan 10, 2026 • 2:00 PM – 3:00 PM"
 * - "Sat, Jan 10, 2026 • 2:00 PM"
 * - "Sat, Jan 10, 2026 2:00 PM → Sun, Jan 11, 2026 3:00 PM"
 *
 * @param {Date} startDate
 * @param {Date} [endDate]
 * @returns {string}
 */
export function formatWhenRange(startDate, endDate) {
  if (!startDate) return "";

  const dateFmt = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const timeFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  const day = dateFmt.format(startDate);
  const startTime = timeFmt.format(startDate);

  if (!endDate) return `${day} • ${startTime}`;

  const sameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const endTime = timeFmt.format(endDate);

  if (sameDay) return `${day} • ${startTime} – ${endTime}`;

  const endDay = dateFmt.format(endDate);
  return `${day} ${startTime} → ${endDay} ${endTime}`;
}
